import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { sessionUserFromIdToken } from './claims';
import type { AuthProvider, AuthSession, SignInInput, SignInResult } from './provider';

/**
 * Real Amazon Cognito User Pool auth provider (browser-safe; the app client has
 * NO secret). Built on `amazon-cognito-identity-js`, which persists tokens in
 * `localStorage` and handles refresh-token exchange. We expose a small
 * promise-based surface ({@link AuthProvider}) over its callback API.
 *
 * This is NOT security: the AWS API Gateway authorizer verifies the JWT on
 * every call. We inject the **ID token** as the bearer because the backend
 * reads `custom:*` claims from it.
 */
export interface CognitoConfig {
  userPoolId: string;
  userPoolClientId: string;
}

/** Map a CognitoUserSession to our AuthSession, or null if no usable role claim. */
function toAuthSession(session: CognitoUserSession): AuthSession | null {
  const idToken = session.getIdToken();
  const jwt = idToken.getJwtToken();
  const user = sessionUserFromIdToken(jwt);
  if (!user) return null;
  return { user, idToken: jwt, expiresAt: idToken.getExpiration() };
}

/**
 * Translate Cognito error codes/names to Spanish, user-facing copy. Unknown
 * errors fall back to a generic message (never leak raw AWS strings to the UI).
 */
export function cognitoErrorToSpanish(err: unknown): string {
  const code =
    (err as { code?: string; name?: string } | null)?.code ??
    (err as { name?: string } | null)?.name ??
    '';
  switch (code) {
    case 'NotAuthorizedException':
      return 'Correo o contraseña incorrectos.';
    case 'UserNotFoundException':
      return 'No existe una cuenta con ese correo.';
    case 'UserNotConfirmedException':
      return 'Tu cuenta aún no está confirmada. Contacta al administrador.';
    case 'PasswordResetRequiredException':
      return 'Debes restablecer tu contraseña. Contacta al administrador.';
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return 'Demasiados intentos. Espera un momento e intenta de nuevo.';
    case 'NetworkError':
      return 'No se pudo conectar con el servidor. Revisa tu conexión a internet.';
    default:
      return 'No se pudo iniciar sesión. Intenta de nuevo.';
  }
}

export function createCognitoProvider(config: CognitoConfig): AuthProvider {
  const pool = new CognitoUserPool({
    UserPoolId: config.userPoolId,
    ClientId: config.userPoolClientId,
  });

  const makeUser = (email: string): CognitoUser =>
    new CognitoUser({ Username: email, Pool: pool });

  /** Resolve the cached session for the current user, refreshing if expired. */
  const getCurrentSession = (): Promise<CognitoUserSession | null> =>
    new Promise((resolve) => {
      const user = pool.getCurrentUser();
      if (!user) {
        resolve(null);
        return;
      }
      // getSession transparently uses the refresh token when the ID/access
      // tokens are expired, so a returned session is always currently valid.
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }
        resolve(session);
      });
    });

  return {
    mode: 'cognito',

    async restore() {
      const session = await getCurrentSession();
      return session ? toAuthSession(session) : null;
    },

    signIn({ email, password }: SignInInput): Promise<SignInResult> {
      return new Promise((resolve, reject) => {
        const user = makeUser(email);
        const details = new AuthenticationDetails({ Username: email, Password: password });
        user.authenticateUser(details, {
          onSuccess: (session) => {
            const mapped = toAuthSession(session);
            if (!mapped) {
              // Authenticated but the token carries no role this app understands.
              reject(new Error('Tu cuenta no tiene un rol asignado. Contacta al administrador.'));
              return;
            }
            resolve({ status: 'ok', session: mapped });
          },
          onFailure: (err) => {
            reject(new Error(cognitoErrorToSpanish(err)));
          },
          newPasswordRequired: () => {
            // First-login: a permanent password must be set. We don't collect
            // it here — surface a clear status the UI explains in Spanish.
            resolve({ status: 'new_password_required' });
          },
        });
      });
    },

    async getValidIdToken() {
      const session = await getCurrentSession();
      return session ? session.getIdToken().getJwtToken() : null;
    },

    signOut(): Promise<void> {
      return new Promise((resolve) => {
        const user = pool.getCurrentUser();
        if (!user) {
          resolve();
          return;
        }
        user.signOut(() => resolve());
      });
    },
  };
}
